import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useAuth } from "./AuthContext";
import { useUserRole } from "./UserRoleContext";
import io from "socket.io-client";

const MessagingContext = createContext();

/** Normalize conversation ids from API vs URL (number/string mismatch). */
function normalizeConversationId(id) {
  if (id == null) return id;
  const n = Number(id);
  return Number.isNaN(n) ? id : n;
}

function conversationIdEqual(a, b) {
  if (a == null || b == null) return a === b;
  return Number(a) === Number(b);
}

export const useMessaging = () => {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error("useMessaging must be used within a MessagingProvider");
  }
  return context;
};

export const MessagingProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { userData } = useUserRole();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const activeConversationRef = useRef(activeConversation);
  activeConversationRef.current = activeConversation;

  const [messages, setMessages] = useState({});
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const markConversationAsReadRef = useRef(async () => {});
  const [unreadCount, setUnreadCount] = useState(0);
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState({}); // Track loading per conversation
  const [typingUsers, setTypingUsers] = useState({});

  // Fetch conversations from API
  const fetchConversations = useCallback(async () => {
    if (!currentUser) return;

    try {
      const response = await fetch("/api/messaging/conversations", {
        credentials: "include",
      });
      const data = await response.json();

      if (data.success) {
        setConversations(data.conversations);

        // Calculate unread count
        const totalUnread = data.conversations.reduce(
          (sum, conv) => sum + (conv.unread_count || 0),
          0,
        );
        setUnreadCount(totalUnread);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Initialize socket connection
  useEffect(() => {
    if (!currentUser || !userData) return;

    fetchConversations();

    const newSocket = io("/", {
      withCredentials: true,
      transports: ["websocket", "polling"],
    });

    newSocket.on("connect", () => {
      console.log("Messaging socket connected:", newSocket.id);
    });

    // Listen for new messages
    newSocket.on("new_message", ({ conversationId, message }) => {
      console.log("New message received:", message);

      // Clear loading state if this is the first message
      setLoadingMessages((prev) => {
        if (prev[conversationId]) {
          return {
            ...prev,
            [conversationId]: false,
          };
        }
        return prev;
      });

      // Add message to messages array
      setMessages((prev) => ({
        ...prev,
        [conversationId]: [...(prev[conversationId] || []), message],
      }));

      // Update conversation last message and re-sort
      setConversations((prev) => {
        const updated = prev.map((conv) =>
          conversationIdEqual(conv.conversation_id, conversationId)
            ? {
                ...conv,
                last_message_at: message.created_at,
                last_message_text: message.message_text,
                unread_count:
                  message.sender_id !== userData.user_id
                    ? (conv.unread_count || 0) + 1
                    : conv.unread_count,
              }
            : conv,
        );

        // Sort: pinned first, then pinned_order ASC, then last_message_at DESC
        return updated.sort((a, b) => {
          const aPinned = a.is_pinned ? 1 : 0;
          const bPinned = b.is_pinned ? 1 : 0;
          if (bPinned !== aPinned) return bPinned - aPinned;
          if (a.is_pinned && b.is_pinned) {
            const aOrder = a.pinned_order ?? 999999;
            const bOrder = b.pinned_order ?? 999999;
            return aOrder - bOrder;
          }
          const aTime = new Date(a.last_message_at || a.created_at || 0);
          const bTime = new Date(b.last_message_at || b.created_at || 0);
          return bTime - aTime;
        });
      });

      // Update unread count if message is not from current user
      if (message.sender_id !== userData.user_id) {
        setUnreadCount((prev) => prev + 1);
      }
    });

    // Listen for read receipt updates
    newSocket.on("messages_read", ({ conversationId, messageIds, readBy }) => {
      // Update read receipts for messages
      setMessages((prev) => {
        const conversationMessages = prev[conversationId] || [];
        const updatedMessages = conversationMessages.map((msg) => {
          if (
            messageIds.includes(msg.message_id) &&
            msg.sender_id === userData.user_id
          ) {
            return {
              ...msg,
              read_by_recipient: true,
              read_at: new Date().toISOString(),
            };
          }
          return msg;
        });
        return {
          ...prev,
          [conversationId]: updatedMessages,
        };
      });
    });

    // Typing indicators
    newSocket.on("user_typing", ({ userId, conversationId }) => {
      setTypingUsers((prev) => ({
        ...prev,
        [conversationId]: [...(prev[conversationId] || []), userId],
      }));
    });

    newSocket.on("user_stopped_typing", ({ userId, conversationId }) => {
      setTypingUsers((prev) => ({
        ...prev,
        [conversationId]: (prev[conversationId] || []).filter(
          (id) => id !== userId,
        ),
      }));
    });

    // Update unread messages
    newSocket.on("update_unread_messages", ({ conversationId }) => {
      fetchConversations();
    });

    // Conversation renamed by any participant
    newSocket.on("conversation_renamed", ({ conversationId, title, displayName }) => {
      setConversations((prev) =>
        prev.map((conv) =>
          conv.conversation_id === conversationId
            ? {
                ...conv,
                title,
                display_name: displayName,
              }
            : conv,
        ),
      );
    });

    newSocket.on("connect_error", (error) => {
      console.error("Messaging socket error:", error);
    });

    setSocket(newSocket);

    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [currentUser, userData, fetchConversations]);

  useEffect(() => {
    const onResync = () => {
      fetchConversations();
    };
    window.addEventListener("messagingResync", onResync);
    return () => window.removeEventListener("messagingResync", onResync);
  }, [fetchConversations]);

  // Create group conversation
  const createGroupConversation = useCallback(
    async (participantIds, title = null) => {
      try {
        const response = await fetch("/api/messaging/conversations/group", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ participantIds, title }),
        });

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || "Failed to create group");
        }

        const conversation = data.conversation;
        setConversations((prev) => {
          const exists = prev.find(
            (c) => c.conversation_id === conversation.conversation_id,
          );
          if (exists) return prev;
          return [conversation, ...prev];
        });

        return conversation;
      } catch (error) {
        console.error("Error creating group conversation:", error);
        throw error;
      }
    },
    [],
  );

  // Get or create direct conversation
  const getOrCreateConversation = useCallback(
    async (recipientId) => {
      try {
        const response = await fetch("/api/messaging/conversations/direct", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ recipientId }),
        });

        const data = await response.json();

        if (data.success) {
          if (data.isNew) {
            // Add new conversation to the list
            setConversations((prev) => {
              // Check if it already exists
              const exists = prev.find(
                (c) => c.conversation_id === data.conversation.conversation_id,
              );
              if (exists) {
                return prev;
              }
              return [data.conversation, ...prev];
            });
          } else {
            // Existing conversation - refresh conversations list to get latest data
            fetchConversations();
          }
          return data.conversation;
        }
      } catch (error) {
        console.error("Error creating conversation:", error);
        throw error;
      }
    },
    [fetchConversations],
  );

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (conversationId, before = null) => {
    // Set loading state for this conversation
    if (!before) {
      setLoadingMessages((prev) => ({
        ...prev,
        [conversationId]: true,
      }));
    }

    try {
      const url = before
        ? `/api/messaging/conversations/${conversationId}/messages?before=${before}`
        : `/api/messaging/conversations/${conversationId}/messages`;

      const response = await fetch(url, {
        credentials: "include",
      });

      const data = await response.json();

      if (data.success) {
        setMessages((prev) => ({
          ...prev,
          [conversationId]: before
            ? [...data.messages, ...(prev[conversationId] || [])]
            : data.messages,
        }));

        // Clear loading state
        if (!before) {
          setLoadingMessages((prev) => ({
            ...prev,
            [conversationId]: false,
          }));
        }

        return data.messages;
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
      // Clear loading state on error
      if (!before) {
        setLoadingMessages((prev) => ({
          ...prev,
          [conversationId]: false,
        }));
      }
      throw error;
    }
  }, []);

  // Send message via socket
  const sendMessage = useCallback(
    (conversationId, messageText) => {
      if (socket && messageText.trim()) {
        socket.emit("send_message", {
          conversationId,
          messageText: messageText.trim(),
        });
      }
    },
    [socket],
  );

  // Mark conversation as read (HTTP; works without socket)
  const markConversationAsRead = useCallback(
    async (conversationId) => {
      const cid = normalizeConversationId(conversationId);
      if (cid == null) return;
      try {
        const response = await fetch(
          `/api/messaging/conversations/${cid}/read`,
          {
            method: "PATCH",
            credentials: "include",
          },
        );

        if (response.ok) {
          setConversations((prev) =>
            prev.map((conv) =>
              conversationIdEqual(conv.conversation_id, cid)
                ? {
                    ...conv,
                    unread_count: 0,
                    last_read_at: new Date().toISOString(),
                  }
                : conv,
            ),
          );

          const conv = conversations.find((c) =>
            conversationIdEqual(c.conversation_id, cid),
          );
          if (conv && conv.unread_count > 0) {
            setUnreadCount((prev) => Math.max(0, prev - conv.unread_count));
          }

          await fetchConversations();
        }
      } catch (error) {
        console.error("Error marking conversation as read:", error);
      }
    },
    [conversations, fetchConversations],
  );

  markConversationAsReadRef.current = markConversationAsRead;

  // Join conversation room (when viewing)
  const joinConversation = useCallback(
    (rawConversationId) => {
      const conversationId = normalizeConversationId(rawConversationId);
      if (conversationId == null) return;

      setActiveConversation(conversationId);

      const hasMessages =
        messagesRef.current[conversationId] !== undefined;

      if (!hasMessages) {
        fetchMessages(conversationId).catch((error) => {
          console.error("Error loading messages:", error);
        });
      }

      void markConversationAsReadRef.current(conversationId);

      if (socket) {
        socket.emit("join_conversation", conversationId);
      }
    },
    [socket, fetchMessages],
  );

  // Leave conversation room
  const leaveConversation = useCallback(() => {
    const id = activeConversationRef.current;
    if (socket && id != null) {
      socket.emit(
        "leave_conversation",
        normalizeConversationId(id),
      );
      setActiveConversation(null);
    }
  }, [socket]);

  // Typing indicators
  const startTyping = useCallback(
    (conversationId) => {
      if (socket) {
        socket.emit("typing_start", { conversationId });
      }
    },
    [socket],
  );

  const stopTyping = useCallback(
    (conversationId) => {
      if (socket) {
        socket.emit("typing_stop", { conversationId });
      }
    },
    [socket],
  );

  // Archive conversation
  const archiveConversation = useCallback(
    async (conversationId, archived = true) => {
      try {
        const response = await fetch(
          `/api/messaging/conversations/${conversationId}/archive`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ archived }),
          },
        );

        if (response.ok) {
          setConversations((prev) =>
            prev.filter((conv) => conv.conversation_id !== conversationId),
          );
        }
      } catch (error) {
        console.error("Error archiving conversation:", error);
      }
    },
    [],
  );

  // Pin/unpin conversation (optimistic UI)
  const pinConversation = useCallback(async (conversationId, pinned) => {
    let previousConversations;

    setConversations((prev) => {
      previousConversations = [...prev];
      const updated = prev.map((conv) => {
        if (conv.conversation_id !== conversationId) {
          if (pinned && (conv.is_pinned || conv.is_pinned === 1)) {
            return { ...conv, pinned_order: (conv.pinned_order ?? 0) + 1 };
          }
          return conv;
        }
        return {
          ...conv,
          is_pinned: pinned,
          pinned_order: pinned ? 0 : null,
        };
      });

      return updated.sort((a, b) => {
        const aPinned = a.is_pinned ? 1 : 0;
        const bPinned = b.is_pinned ? 1 : 0;
        if (bPinned !== aPinned) return bPinned - aPinned;
        if (a.is_pinned && b.is_pinned) {
          return (a.pinned_order ?? 999) - (b.pinned_order ?? 999);
        }
        const aTime = new Date(a.last_message_at || a.created_at || 0);
        const bTime = new Date(b.last_message_at || b.created_at || 0);
        return bTime - aTime;
      });
    });

    try {
      const response = await fetch(
        `/api/messaging/conversations/${conversationId}/pin`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ pinned }),
        },
      );

      if (!response.ok) throw new Error("Pin failed");
    } catch (error) {
      console.error("Error pinning conversation:", error);
      setConversations(previousConversations);
      throw error;
    }
  }, []);

  // Reorder pinned conversations (optimistic UI)
  const reorderPinnedConversations = useCallback(async (conversationIds) => {
    let previousConversations;

    setConversations((prev) => {
      previousConversations = [...prev];
      const orderMap = {};
      conversationIds.forEach((id, idx) => {
        orderMap[id] = idx;
      });

      const updated = prev.map((conv) => {
        if (orderMap.hasOwnProperty(conv.conversation_id)) {
          return { ...conv, pinned_order: orderMap[conv.conversation_id] };
        }
        return conv;
      });

      return updated.sort((a, b) => {
        const aPinned = a.is_pinned ? 1 : 0;
        const bPinned = b.is_pinned ? 1 : 0;
        if (bPinned !== aPinned) return bPinned - aPinned;
        if (a.is_pinned && b.is_pinned) {
          return (a.pinned_order ?? 999) - (b.pinned_order ?? 999);
        }
        const aTime = new Date(a.last_message_at || a.created_at || 0);
        const bTime = new Date(b.last_message_at || b.created_at || 0);
        return bTime - aTime;
      });
    });

    try {
      const response = await fetch("/api/messaging/conversations/pin-order", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ conversationIds }),
      });

      if (!response.ok) throw new Error("Reorder failed");
    } catch (error) {
      console.error("Error reordering pinned conversations:", error);
      setConversations(previousConversations);
      throw error;
    }
  }, []);

  // Get recipients list
  const getRecipients = useCallback(async (allowAnyArtist = false) => {
    try {
      const url = allowAnyArtist
        ? `/api/messaging/recipients?allowAnyArtist=true`
        : `/api/messaging/recipients`;
      const response = await fetch(url, {
        credentials: "include",
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching recipients:", error);
      return {
        success: false,
        error: error.message || "Failed to fetch recipients",
      };
    }
  }, []);

  // Search conversations
  const searchConversations = useCallback(async (query) => {
    try {
      const response = await fetch(
        `/api/messaging/conversations/search?q=${encodeURIComponent(query)}`,
        {
          credentials: "include",
        },
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error searching conversations:", error);
      return {
        success: false,
        error: error.message || "Failed to search conversations",
      };
    }
  }, []);

  // Rename group conversation
  const renameGroupConversation = useCallback(async (conversationId, title) => {
    try {
      const response = await fetch(
        `/api/messaging/conversations/${conversationId}/title`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ title }),
        },
      );

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to rename conversation");
      }

      setConversations((prev) =>
        prev.map((conv) =>
          conv.conversation_id === conversationId
            ? {
                ...conv,
                title: data.title,
                display_name: data.displayName,
              }
            : conv,
        ),
      );

      return data;
    } catch (error) {
      console.error("Error renaming conversation:", error);
      throw error;
    }
  }, []);

  // Mark messages as read
  const markMessagesAsRead = useCallback(
    async (conversationId, messageIds = null) => {
      const cid = normalizeConversationId(conversationId);
      if (cid == null) return;
      try {
        if (socket && messageIds && messageIds.length > 0) {
          socket.emit("mark_messages_read", {
            conversationId: cid,
            messageIds,
          });
          await fetchConversations();
        } else {
          const response = await fetch(
            `/api/messaging/conversations/${cid}/messages/read-all`,
            {
              method: "POST",
              credentials: "include",
            },
          );

          if (response.ok) {
            setMessages((prev) => {
              const key =
                Object.keys(prev).find((k) => conversationIdEqual(k, cid)) ??
                cid;
              const conversationMessages = prev[key] || [];
              const updatedMessages = conversationMessages.map((msg) => {
                if (msg.sender_id === userData.user_id) {
                  return msg;
                }
                return {
                  ...msg,
                  read_by_recipient: true,
                  read_at: new Date().toISOString(),
                };
              });
              return {
                ...prev,
                [key]: updatedMessages,
              };
            });
            await fetchConversations();
          }
        }
      } catch (error) {
        console.error("Error marking messages as read:", error);
      }
    },
    [socket, userData, fetchConversations],
  );

  const value = {
    conversations,
    activeConversation,
    messages,
    unreadCount,
    loading,
    loadingMessages,
    socket,
    typingUsers,
    fetchConversations,
    getOrCreateConversation,
    createGroupConversation,
    fetchMessages,
    sendMessage,
    joinConversation,
    leaveConversation,
    startTyping,
    stopTyping,
    markConversationAsRead,
    archiveConversation,
    pinConversation,
    reorderPinnedConversations,
    getRecipients,
    searchConversations,
    renameGroupConversation,
    markMessagesAsRead,
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};
